
import { useState, useEffect, useRef } from 'react';
import { StatsCard } from '@/components/shared/StatsCard'
import { useParams, Link } from 'react-router-dom';
import { formatUnixTimestamp, formatBalanceInMIST, formatBalanceInSUI } from '@/utils/format';

import {
	File,
	FileClock,
	Database,
	Gauge,
	Box,
	Trash,
	CircleUserRound,
	Wallet,
	Fuel,
	Cpu,
	CalendarPlus
} from "lucide-react";

import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'

import {
	Pagination,
	PaginationContent,
	PaginationItem,
	PaginationNext,
	PaginationPrevious,
} from '@/components/ui/pagination'

import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from '@/components/ui/card'

import {
	useBlob,
	useBlobVersions
} from '@/hooks/useApi';
import { useMemo } from 'react';
import { SUI_EXPLORER_BASE_URL } from '@/lib/constants';

const PAGE_SIZE = 20;
const MAX_ENTRIES = 10_000;

interface BlobVersionsProps {
	id: string;
}
const BlobVersions = (props: BlobVersionsProps) => {
	const { id } = props;

	const pageLimit = MAX_ENTRIES / PAGE_SIZE;
	const initialLoadCompleted = useRef<boolean>(false);

	const startingCursor = useRef<any>(null);
	const startingPageData = useRef<any>(null);

	const [cursor, setCursor] = useState<any>(null);
	const [currentPage, setCurrentPage] = useState<number>(1);

	const [pageToRender, setPageToRender] = useState<any>({});
	const [prevCursorHistory, setPrevCursorHistory] = useState<Array<string>>([]);
	const nextCursor = useRef<any>(null);

	const { data: blobVersionData } = useBlobVersions(id);


	useEffect(() => {
		if (!blobVersionData?.cursor) {
			setPageToRender(blobVersionData);
			return;
		}

		if (!initialLoadCompleted.current && blobVersionData?.cursor) {
			startingCursor.current = blobVersionData.cursor;
			startingPageData.current = blobVersionData;
			initialLoadCompleted.current = true;
		}

		if (blobVersionData?.cursor) {
			nextCursor.current = blobVersionData.cursor;
		}

		if (startingCursor.current === nextCursor.current) {
			setPageToRender(startingPageData.current);
		} else {
			setPageToRender(blobVersionData);
		}
	}, [blobVersionData])

	if (!blobVersionData) {
		return <>Loading...</>
	}

	return (
		<>
			<Pagination className='mb-2 border-2 border-black rounded-sm shadow-[3px_3px_0_0_black] px-2 py-1 bg-[#37C5B3]/60 backdrop-blur-md'>
				<PaginationContent>
					{/* Previous Button */}
					<PaginationItem>
						<PaginationPrevious
							className='bg-[#C0E6FF]/10 backdrop-blur-md border-2 border-black text-white rounded-sm hover:bg-[#A56FF1]/40 hover:backdrop-blur-md'
							content={<span className='text-sm text-white'>Prev</span> as any}
							onClick={() => {
								if (currentPage === 1) return;
								if (prevCursorHistory.length === 1) {
									setCursor(null);
								}

								setPrevCursorHistory((prev: any) => {
									const temp = [...prev];
									temp.pop();
									if (temp.length) setCursor(temp[temp.length - 1]);
									return temp;
								})
								setCurrentPage(prev => prev - 1);
							}}
						/>
					</PaginationItem>

					{/* Next Button */}
					<PaginationItem>
						<PaginationNext
							className='bg-[#C0E6FF]/10 backdrop-blur-md border-2 border-black text-white rounded-sm hover:bg-[#A56FF1]/40 hover:backdrop-blur-md'
							content={<span className='text-sm text-white'>Next</span> as any}
							onClick={() => {
								if (currentPage === pageLimit) return;
								if (!nextCursor.current) return;
								setCursor(nextCursor.current);
								setPrevCursorHistory((prev: any) => [...prev, nextCursor.current]);
								setCurrentPage(prev => prev + 1);
							}}
						/>
					</PaginationItem>
				</PaginationContent>
			</Pagination>
			<div className="rounded-sm border-2 border-black shadow-[4px_4px_0_0_black] overflow-hidden">
				<Table>
					<TableHeader className='bg-[#37C5B3]/60 backdrop-blur-md border-b-2 border-black'>
						<TableRow className='hover:bg-[#37C5B3]/60'>
							{[
								'Version', 'Type', 'Digest'
							].map(label => (
								<TableHead key={label} className="text-xs font-bold text-white uppercase">{label}</TableHead>
							))}
						</TableRow>
					</TableHeader>
					<TableBody>
						{pageToRender?.data?.map((blob: any) => (
							<TableRow key={blob.version} className='hover:bg-[#C0E6FF]/20'>
								<TableCell className='text-md text-bold text-white'>{blob.version}</TableCell>
								<TableCell className='text-md text-bold text-white'>{blob.type}</TableCell>
								<TableCell className='text-md text-bold text-white'>
									<Link to={`${SUI_EXPLORER_BASE_URL}/transaction-blocks/${blob.txDigest}`} className="flex items-center space-x-2 underline decoration-2 decoration-[#A56FF1]" target='_blank'>
									<span className='md:hidden'>{`${blob.txDigest.slice(0, 6)}...${blob.txDigest.slice(-6)}`}</span>
									<span className='hidden md:block'>{blob.txDigest}</span>
									</Link>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</div>
		</>
	)

}

export const BlobDetailsPage = () => {
	const { id } = useParams<{ id: string }>()

	if (!id) {
		return <>Not Found</>
	}

	const { data: blobData } = useBlob(id);

	const totalGasCost = useMemo(() => {
		if (!blobData) return 0;

		return (
			Number(blobData?.gasUsed.computationCost) +
			Number(blobData?.gasUsed.storageCost) -
			Number(blobData?.gasUsed.storageRebate)
		);
	}, [blobData])

	if (!blobData) {
		return <>Loading...</>
	}

	return (
		<div className="flex-1 overflow-y-auto overflow-x-hidden flex max-w-full [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
			<div className='flex flex-col gap-1 w-full h-full'>
				<div className="flex-1 flex flex-col gap-6 min-w-0 px-6">
					<StatsCard
						title="Blob ID"
						className='text-sm flex-1'
						valueStyleOverride='text-lg lg:text-2xl overflow-hidden whitespace-nowrap text-ellipsis'
						value={blobData.blobId}
						icon={<File className='w-4 h-4' />}
					/>
				</div>
				<div
					className='grid grid-cols-1 gap-4 px-2 py-2
						md:grid-cols-2
						xl:grid-cols-4 
					'>
					{/* Stat Cards */}
					<div className='col-span-2 py-0'>
						<div
							className='grid grid-cols-1 gap-4 p-4
								md:grid-cols-2 
								xl:grid-cols-4 
							'
						>
							<StatsCard
								title="Object ID"
								className='text-sm xl:col-span-2 md:col-span-1'
								valueStyleOverride='text-lg lg:text-2xl'
								tooltip=''
								value={
									<Link to={`${SUI_EXPLORER_BASE_URL}/objects/${blobData.objectId}`} className="flex items-center space-x-2 text-white underline decoration-2 decoration-[#A56FF1]" target='_blank'>
										<span className='hidden md:block'>{`${blobData.objectId.slice(0, 6)}...${blobData.objectId.slice(-6)}`}</span>
										<span className='md:hidden'>{`${blobData.objectId.slice(0, 6)}...`}</span>
									</Link>
								}
								icon={<Box className="h-4 w-4" />}
							/>
							<StatsCard
								title="Owner"
								className='text-sm xl:col-span-2 md:col-span-1'
								valueStyleOverride='text-lg lg:text-2xl'
								tooltip=''
								value={
									<Link to={`${SUI_EXPLORER_BASE_URL}/accounts/${blobData.sender}`} className="flex items-center space-x-2 text-white underline decoration-2 decoration-[#A56FF1]" target='_blank'>
										<span className='hidden md:block'>{`${blobData.sender.slice(0, 6)}...${blobData.sender.slice(-6)}`}</span>
										<span className='md:hidden'>{`${blobData.sender.slice(0, 6)}...`}</span>
									</Link>
								}
								icon={<CircleUserRound className="h-4 w-4" />}
							/>
							<StatsCard
								title="Creation Time"
								className='text-sm xl:col-span-4 md:col-span-2'
								valueStyleOverride='text-lg lg:text-2xl'
								value={formatUnixTimestamp(Number(blobData.timestampMS))}
								tooltip=''
								icon={<CalendarPlus className="h-4 w-4" />}
							/>

							<div className='grid grid-cols-1 gap-4
								md:grid-cols-3
								xl:grid-cols-3 
								xl:col-span-4
								md:col-span-2
							'>
								<StatsCard
									title="Size"
									className='text-sm'
									valueStyleOverride='text-lg lg:text-2xl'
									value={blobData.size}
									tooltip=''
									icon={<File className="h-4 w-4" />}
								/>
								<StatsCard
									title="Starting Epoch"
									className='text-sm'
									valueStyleOverride='text-lg lg:text-2xl'
									tooltip=''
									value={blobData.startEpoch}
									icon={<CalendarPlus className="h-4 w-4" />}
								/>
								<StatsCard
									title="Ending Epoch"
									className='text-sm'
									valueStyleOverride='text-lg lg:text-2xl'
									value={blobData.endEpoch}
									tooltip=''
									icon={<Trash className="h-4 w-4" />}
								/>
							</div>
						</div>
					</div>

					{/* Gas Usage */}
					<div className='col-span-2 xl:px-2 md:px-2 py-2'>
						<Card className="border-2 border-black rounded-sm shadow-[4px_4px_0_0_black] bg-[#0C0F1D]/40 backdrop-blur-md">
							<CardHeader className="flex flex-col justify-start space-y-0 p-0 min-w-60">
								<div className='flex flex-row items-center px-2 pt-2'>
									<div className="rounded-full px-2 transition-colors">
										<Fuel className='h-4 w-4 text-white' />
									</div>
									<div className='flex-1'>
										<CardTitle className="text-sm md:text-md lg:text-lg font-medium text-white">Gas Usage</CardTitle>
									</div>
								</div>
								<div className='px-4'>
									<CardDescription className='text-xs lg:text-md text-white'>
										Gas cost breakdown for the transaction that certified this blob.
									</CardDescription>
								</div>
							</CardHeader>
							<CardContent className='pt-4'>
								<div className='flex flex-col gap-3'>
									<div className='grid grid-cols-1 md:grid-cols-2 gap-2'>
										<StatsCard
											className='text-sm'
											valueStyleOverride='text-md xl:text-xl'
											title="Total Cost"
											value={
												<>
													<div className='text-md'>
														{totalGasCost ? formatBalanceInSUI(Number(totalGasCost)) : '--'} SUI
													</div>
													<div className='text-sm text-white/60'>
														{totalGasCost ? formatBalanceInMIST(Number(totalGasCost)) : '--'} MIST
													</div>
												</>
											}
											icon={<Wallet className='h-5 w-5' />}
										/>
										<StatsCard
											title="Computation Cost"
											className='text-sm'
											valueStyleOverride='text-md xl:text-xl'
											value={
												<>
													<div className='text-md'>
														{blobData?.gasUsed?.computationCost ? formatBalanceInSUI(Number(blobData?.gasUsed?.computationCost)) : '--'} SUI
													</div>
													<div className='text-sm text-white/60'>
														{blobData?.gasUsed?.computationCost ? formatBalanceInMIST(Number(blobData?.gasUsed?.computationCost)) : '--'} MIST
													</div>
												</>
											}
											icon={<Cpu className='h-5 w-5' />}
										/>
									</div>
									<div className='grid grid-cols-1 md:grid-cols-3 gap-2'>
										<StatsCard
											title="Storage Cost"
											className='text-sm'
											valueStyleOverride='text-md xl:text-xl'
											value={
												<>
													<div className='text-md'>
														{blobData?.gasUsed?.storageCost ? formatBalanceInSUI(Number(blobData?.gasUsed?.storageCost)) : '--'} SUI
													</div>
													<div className='text-sm text-white/60'>
														{blobData?.gasUsed?.storageCost ? formatBalanceInMIST(Number(blobData?.gasUsed?.storageCost)) : '--'} MIST
													</div>
												</>
											}
											icon={<Database className='h-5 w-5' />}
										/>
										<StatsCard
											title="Storage Rebate"
											className='text-sm'
											valueStyleOverride='text-md xl:text-xl'
											value={
												<>
													<div className='text-md'>
														{blobData?.gasUsed?.storageRebate ? formatBalanceInSUI(Number(blobData?.gasUsed?.storageRebate)) : '--'} SUI
													</div>
													<div className='text-sm text-white/60'>
														{blobData?.gasUsed?.storageRebate ? formatBalanceInMIST(Number(blobData?.gasUsed?.storageRebate)) : '--'} MIST
													</div>
												</>
											}
											icon={<Database className='h-5 w-5' />}
										/>
										<StatsCard
											title="Non-Refundable Fee"
											className='text-sm'
											valueStyleOverride='text-md xl:text-xl'
											value={
												<>
													<div className='text-md'>
														{blobData?.gasUsed?.nonRefundableStorageFee ? formatBalanceInSUI(Number(blobData?.gasUsed?.nonRefundableStorageFee)) : '--'} SUI
													</div>
													<div className='text-sm text-white/60'>
														{blobData?.gasUsed?.nonRefundableStorageFee ? formatBalanceInMIST(Number(blobData?.gasUsed?.nonRefundableStorageFee)) : '--'} MIST
													</div>
												</>
											}
											icon={<Database className='h-5 w-5' />}
										/>
									</div>
								</div>
							</CardContent>
						</Card>
					</div>
				</div>
				{/* Versions */}
				<div className='px-4 pb-4'>
					<Card className="bg-[#0C0F1D]/70 backdrop-blur-md border-2 border-black shadow-[4px_4px_0_0_black] rounded-sm">
						<CardHeader className="p-0">
							<div className="flex items-center gap-2 px-2 pt-2">
								<FileClock className="h-5 w-5 text-white" />
								<CardTitle className="text-lg font-bold text-white">Versions</CardTitle>
							</div>
							<CardDescription className="text-xs px-4 pb-2 text-white">
								Other versions of this blob.
							</CardDescription>
						</CardHeader>
						<CardContent className="px-2">
							<BlobVersions id={id} />
						</CardContent>
					</Card>
				</div>

			</div>

		</div>
	);
}